import type { CatalogCategory, CatalogProductCard } from '@web/widgets/product-card/type/product-card-widget.type';

/**
 * Ids of the product categories the online store allows on this site.
 * `null` means the list is not known yet — either still loading or the request failed.
 * Nothing may be rendered in that state: an unknown list must never be read as "everything is allowed",
 * otherwise a single failed request publishes the categories the store owner keeps unpublished.
 */
export type EnabledCategoryIds = number[] | null;

/** How the enabled categories arrive from the API — plain ids or category objects. */
export type LandingProductCategory = number | { id?: number };

/**
 * Reads the enabled ids out of the `categories` field of the API payload.
 * Anything but an array answers `null`: an unexpected payload is an unknown list, not an empty one.
 */
export function toEnabledCategoryIds(
    categories: (LandingProductCategory | null | undefined)[] | null | undefined
): EnabledCategoryIds {
    if (!Array.isArray(categories)) {
        return null;
    }

    return categories
        .map((category) => (typeof category === 'number' ? category : category?.id))
        .filter((id): id is number => typeof id === 'number');
}

/** Whether the store allows at least one category, so the widget has something to show. */
export function hasEnabledCategories(enabledIds: EnabledCategoryIds): boolean {
    return Boolean(enabledIds?.length);
}

export function isCategoryEnabled(category: CatalogCategory, enabledIds: EnabledCategoryIds): boolean {
    return Boolean(enabledIds?.includes(category.id));
}

export function hasEnabledCategory(category: CatalogCategory, enabledIds: EnabledCategoryIds): boolean {
    return (
        isCategoryEnabled(category, enabledIds) ||
        !!category.children?.some((child) => hasEnabledCategory(child, enabledIds))
    );
}

/** Keeps only the branches that hold at least one enabled category, so disabled ones never reach the markup. */
export function filterEnabledCategories(
    categories: CatalogCategory[],
    enabledIds: EnabledCategoryIds
): CatalogCategory[] {
    if (!hasEnabledCategories(enabledIds)) {
        return [];
    }

    return (categories ?? [])
        .filter((category) => hasEnabledCategory(category, enabledIds))
        .map((category) => ({
            ...category,
            children: category.children ? filterEnabledCategories(category.children, enabledIds) : category.children,
        }));
}

export function findCategoryById(categories: CatalogCategory[], id: number): CatalogCategory | null {
    for (const category of categories ?? []) {
        if (category.id === id) {
            return category;
        }

        const foundInChildren = findCategoryById(category.children ?? [], id);
        if (foundInChildren) {
            return foundInChildren;
        }
    }

    return null;
}

/**
 * Collects ids of the categories a product is allowed to belong to.
 *
 * Without a selected category the whole enabled list is allowed — "All categories" means
 * "every category the store publishes", not "the store publishes every category".
 * With a selected category only its enabled subtree is allowed, because a parent request also
 * returns products of its children and the disabled ones have to be dropped.
 *
 * The enabled ids are the source of truth rather than the category tree: the tree arrives in a
 * separate request and an empty one must not silently allow nothing.
 */
export function collectEnabledCategoryIds(
    categories: CatalogCategory[],
    enabledIds: EnabledCategoryIds,
    selectedCategoryId: number | null = null
): Set<number> {
    if (!hasEnabledCategories(enabledIds)) {
        return new Set<number>();
    }

    if (selectedCategoryId === null) {
        return new Set(enabledIds);
    }

    const selectedCategory = findCategoryById(categories ?? [], selectedCategoryId);

    if (!selectedCategory) {
        // The tree is unknown or the category is gone — only the selected one itself can be allowed.
        return new Set(enabledIds.filter((id) => id === selectedCategoryId));
    }

    const allowedIds = new Set<number>();
    const collect = (list: CatalogCategory[]): void => {
        (list ?? []).forEach((category) => {
            if (enabledIds.includes(category.id)) {
                allowedIds.add(category.id);
            }

            collect(category.children ?? []);
        });
    };

    collect([selectedCategory]);

    return allowedIds;
}

/**
 * Tells whether every category of the given scope is enabled, so nothing has to be filtered out
 * and server side pagination can be used as is.
 *
 * An empty tree answers `false`: a scope that is not known cannot be proven fully enabled, and a
 * selected category that is missing from the tree is not fully enabled either — its products have
 * to be filtered rather than shown.
 */
export function isSubtreeFullyEnabled(
    categories: CatalogCategory[],
    enabledIds: EnabledCategoryIds,
    selectedCategoryId: number | null = null
): boolean {
    if (!hasEnabledCategories(enabledIds) || !(categories ?? []).length) {
        return false;
    }

    const isFullyEnabled = (list: CatalogCategory[]): boolean =>
        (list ?? []).every((category) => enabledIds.includes(category.id) && isFullyEnabled(category.children ?? []));

    if (selectedCategoryId === null) {
        return isFullyEnabled(categories);
    }

    const selectedCategory = findCategoryById(categories, selectedCategoryId);

    return selectedCategory ? isFullyEnabled([selectedCategory]) : false;
}

export function filterProductsByEnabledCategories(
    products: CatalogProductCard[],
    allowedCategoryIds: Set<number>
): CatalogProductCard[] {
    return (products ?? []).filter((product) => allowedCategoryIds.has(product.categoryId));
}
